import React from 'react';
import { Drawer, Button, Spin } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
// Api
import { GET_CHANNEL_MONETIZATION_APPLICATION_CONTENT } from 'api/channelMonetization/queries';
// Types
import {
  GetChannelMonetizationApplicationContent,
  GetChannelMonetizationApplicationContentVariables,
  GetChannelMonetizationApplicationContent_getChannelMonetizationApplicationContent_interviewStreams as ContentItem,
} from 'api/channelMonetization/types/GetChannelMonetizationApplicationContent';
// Styles
import styles from './ReviewContentDrawer.module.scss';

type ReviewContentDrawerProps = {
  applicationId: string | null;
  applicantName: string;
  open: boolean;
  onClose: () => void;
};

const ContentSection: React.FC<{ title: string; items: ContentItem[] }> = ({
  title,
  items,
}) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>
      <span>{title}</span>
      <span className={styles.countBadge}>{items.length}</span>
    </div>
    {items.length === 0 ? (
      <div className={styles.empty}>No content in this period.</div>
    ) : (
      items.map((item) => (
        <div key={item.id} className={styles.item}>
          <div className={styles.itemLabel}>
            <span>{item.label}</span>
            <span className={styles.itemKind}>
              {item.kind}
              {item.date
                ? ` · ${new Date(item.date).toLocaleDateString()}`
                : ''}
            </span>
          </div>
          {item.url && (
            <Button
              type="link"
              icon={<LinkOutlined />}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View
            </Button>
          )}
        </div>
      ))
    )}
  </div>
);

const ReviewContentDrawer: React.FC<ReviewContentDrawerProps> = ({
  applicationId,
  applicantName,
  open,
  onClose,
}) => {
  const { data, loading } = useQuery<
    GetChannelMonetizationApplicationContent,
    GetChannelMonetizationApplicationContentVariables
  >(GET_CHANNEL_MONETIZATION_APPLICATION_CONTENT, {
    variables: { applicationId: applicationId ?? '' },
    skip: !open || !applicationId,
    fetchPolicy: 'cache-and-network',
  });

  const content = data?.getChannelMonetizationApplicationContent;

  return (
    <Drawer
      title={`Review Content — ${applicantName}`}
      placement="right"
      width={520}
      visible={open}
      onClose={onClose}
      destroyOnClose
    >
      {loading && !content ? (
        <Spin />
      ) : content ? (
        <>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <span>Followers ({content.monthLabel})</span>
            </div>
            <div className={styles.followersBig}>{content.followersCount}</div>
          </div>

          <ContentSection
            title="Interview / Stream"
            items={content.interviewStreams}
          />
          <ContentSection title="Interview Now" items={content.interviewNow} />
          <ContentSection
            title="Brand & Athlete Mentions"
            items={content.mentions}
          />
          <ContentSection
            title="New Guest Invites"
            items={content.guestInvites}
          />
        </>
      ) : (
        <div className={styles.empty}>No content found.</div>
      )}
    </Drawer>
  );
};

export default ReviewContentDrawer;
